import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import toast from '../utils/toastUtils';
import SEO from '../components/SEO';

const SEND_CONTACT_URL = process.env.REACT_APP_SEND_CONTACT_URL || '';

interface ContactFormData {
  name: string;
  email: string;
  message: string;
}

// ── Shared form UI ────────────────────────────────────────────────────────────

function ContactForm({
  theme,
  isInsideApp,
}: {
  theme: any;
  isInsideApp: boolean;
}) {
  const [form, setForm] = useState<ContactFormData>({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Get user details at submit time so MSAL has fully resolved
  const { userId, userEmail } = useAuth();

  const handleChange = (field: keyof ContactFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(SEND_CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          userId: userId || null,
          accountEmail: userEmail || null,
        }),
      });

      if (!response.ok) throw new Error('Failed to send');

      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    background: theme.colors.bgPrimary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    color: theme.colors.textPrimary,
    fontSize: '15px',
    boxSizing: 'border-box' as const,
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    color: theme.colors.textSecondary,
    fontSize: '13px',
    fontWeight: 500,
    display: 'block' as const,
    marginBottom: '6px',
  };

  if (submitted) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px' }}>✉️</div>
        <h3 style={{ color: theme.colors.textPrimary, fontSize: '22px', fontWeight: 700, marginBottom: '12px' }}>
          Message sent!
        </h3>
        <p style={{ color: theme.colors.textSecondary, fontSize: '15px', lineHeight: 1.6, margin: 0 }}>
          Thanks for reaching out. We'll get back to you at <strong style={{ color: theme.colors.textPrimary }}>{form.email}</strong> within 1 business day.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Name */}
      <div>
        <label style={labelStyle}>Name</label>
        <input
          type="text"
          placeholder="Your name"
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          disabled={submitting}
          style={inputStyle}
          onFocus={(e) => e.currentTarget.style.borderColor = theme.colors.brand}
          onBlur={(e) => e.currentTarget.style.borderColor = theme.colors.border}
        />
      </div>

      {/* Email */}
      <div>
        <label style={labelStyle}>Email</label>
        <input
          type="email"
          placeholder="your@email.com"
          value={form.email}
          onChange={(e) => handleChange('email', e.target.value)}
          disabled={submitting}
          style={inputStyle}
          onFocus={(e) => e.currentTarget.style.borderColor = theme.colors.brand}
          onBlur={(e) => e.currentTarget.style.borderColor = theme.colors.border}
        />
      </div>

      {/* Message */}
      <div>
        <label style={labelStyle}>Message</label>
        <textarea
          placeholder="How can we help you?"
          value={form.message}
          onChange={(e) => handleChange('message', e.target.value)}
          disabled={submitting}
          rows={5}
          style={{
            ...inputStyle,
            resize: 'vertical',
            fontFamily: 'inherit',
            minHeight: '120px',
          }}
          onFocus={(e) => e.currentTarget.style.borderColor = theme.colors.brand}
          onBlur={(e) => e.currentTarget.style.borderColor = theme.colors.border}
        />
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting || !form.name || !form.email || !form.message}
        style={{
          background: theme.gradients.brand,
          border: 'none',
          color: '#fff',
          padding: '14px 32px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: 600,
          cursor: submitting || !form.name || !form.email || !form.message ? 'not-allowed' : 'pointer',
          opacity: submitting || !form.name || !form.email || !form.message ? 0.6 : 1,
          transition: 'transform 0.2s, box-shadow 0.2s',
          width: '100%',
        }}
        onMouseEnter={(e) => {
          if (!submitting) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(157,78,221,0.4)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {submitting ? '⏳ Sending...' : 'Send Message →'}
      </button>
    </div>
  );
}

// ── Public version (landing page / not logged in) ─────────────────────────────

export function ContactUsPublic() {
  const { theme } = useTheme();
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgPrimary, color: theme.colors.textPrimary }}>
      <SEO
        title="Contact Us | Trubonn"
        description="Get in touch with Trubonn's Australian-based support team."
        path="/contact"
      />

      {/* Nav */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        position: 'sticky',
        top: 0,
        background: theme.colors.bgPrimary,
        zIndex: 1000,
      }}>
        <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/logo.svg" alt="Trubonn Logo" style={{ height: '70px', width: '180px' }} />
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '15px', padding: '8px 16px' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            Home
          </button>
          <button
            onClick={() => navigate('/pricing')}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '15px', padding: '8px 16px' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            Pricing
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '80px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontSize: '40px', fontWeight: 800, marginBottom: '16px',
            background: theme.gradients.brand,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Contact Us
          </h1>
          <p style={{ color: theme.colors.textSecondary, fontSize: '18px', lineHeight: 1.6, margin: 0 }}>
            Have a question or need help? We'd love to hear from you.
          </p>
        </div>

        <div style={{
          background: theme.colors.bgSecondary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '16px',
          padding: '40px',
        }}>
          <ContactForm theme={theme} isInsideApp={false} />
        </div>

        <p style={{ color: theme.colors.textSecondary, fontSize: '13px', textAlign: 'center', marginTop: '24px' }}>
          Or email us directly at{' '}
          <a href="mailto:admin@trubonn.com.au" style={{ color: theme.colors.brand }}>
            admin@trubonn.com.au
          </a>
        </p>
      </div>
    </div>
  );
}

// ── In-app version (inside authenticated app) ─────────────────────────────────

export default function ContactUsPage() {
  const { theme } = useTheme();

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '32px', fontWeight: 800, marginBottom: '8px',
          background: theme.gradients.brand,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}>
          Contact Us
        </h1>
        <p style={{ color: theme.colors.textSecondary, fontSize: '16px', margin: 0 }}>
          Have a question or need help? Send us a message and we'll get back to you within 1 business day.
        </p>
      </div>

      <div style={{ maxWidth: '600px' }}>
        <div style={{
          background: theme.colors.bgSecondary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '16px',
          padding: '40px',
        }}>
          <ContactForm theme={theme} isInsideApp={true} />
        </div>

        <p style={{ color: theme.colors.textSecondary, fontSize: '13px', marginTop: '16px' }}>
          Or email us directly at{' '}
          <a href="mailto:admin@trubonn.com.au" style={{ color: theme.colors.brand }}>
            admin@trubonn.com.au
          </a>
        </p>
      </div>
    </div>
  );
}
